import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';

import api from '../../services/api';
import usePermissions from '../../hooks/usePermissions';
import useNavigationGating from '../../hooks/useNavigationGating';
import { sidebarSections, CHILD_NAV_KEYS } from '../../components/layout/ModernMainLayout';
import { getRecentPages } from '../../utils/recentPages';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PercentIcon from '@mui/icons-material/Percent';
import BarChartIcon from '@mui/icons-material/BarChart';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SavingsIcon from '@mui/icons-material/Savings';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GridViewIcon from '@mui/icons-material/GridView';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/* --------------------------------------------------------------------------
 * Accounting - bento launcher
 *
 * A mosaic of the Accounting module's own menu groups, sized by how much of
 * the module each one carries. It is NOT a second menu definition: the tiles
 * are built from `sidebarSections` - the same constant the left sidebar
 * renders - and every entry passes through the same permission + navigation
 * registry check the sidebar applies, so the two can never disagree about what
 * a user may reach. Adding a page to the sidebar adds it here.
 *
 * Counts on the tiles are counts of what THIS user can see, not the shipped
 * totals; a group with nothing visible is dropped rather than shown empty.
 * ------------------------------------------------------------------------ */

const ACCENT = '#5980a6';
const ACCENT_600 = '#597ea3';
const ACCENT_700 = '#416180';
const ACCENT_900 = '#1d2d3d';
const ACCENT_300 = '#b5d9fd';
const ACCENT_400 = '#94bce3';
const INK = '#1d1f20';
const INK_700 = '#5d5d60';
const INK_900 = '#2b2b2d';
const DIVIDER = 'rgba(29,31,32,.16)';
const PAGE_BG = '#F5F7FA';

// The blueprint frame from the design system: a square, hairline-bordered
// surface with registration marks at each corner.
const blueprintSx = {
  position: 'relative',
  bgcolor: '#fff',
  border: `1px solid ${DIVIDER}`,
  borderRadius: 0,
  '& .corner': {
    position: 'absolute',
    width: '11px',
    height: '11px',
    color: 'rgba(29,31,32,.55)',
    pointerEvents: 'none',
    '&::before, &::after': { content: '""', position: 'absolute', bgcolor: 'currentColor' },
    '&::before': { left: '5px', top: 0, width: '1px', height: '100%' },
    '&::after': { top: '5px', left: 0, width: '100%', height: '1px' },
  },
  '& .corner.tl': { top: '-6px', left: '-6px' },
  '& .corner.tr': { top: '-6px', right: '-6px' },
  '& .corner.bl': { bottom: '-6px', left: '-6px' },
  '& .corner.br': { bottom: '-6px', right: '-6px' },
};

const Corners = () => (
  <>
    <i className="corner tl" /><i className="corner tr" />
    <i className="corner bl" /><i className="corner br" />
  </>
);

// Tile shape per menu group. `order` is the grid flow position; a group that is
// not listed here still renders - as a small tile at the end - so a new sidebar
// group never silently disappears from this page.
const TILE_SPEC = {
  'General Ledger': { order: 10, kind: 'hero', icon: AccountBalanceIcon, blurb: 'Company, fiscal years, postings, statements' },
  'Payables': { order: 20, kind: 'wide', icon: CreditCardIcon },
  'Receivables': { order: 30, kind: 'wide', icon: ReceiptLongIcon },
  'Bank': { order: 40, kind: 'small', icon: AccountBalanceWalletIcon, blurb: 'Book / Recon / Instruments' },
  'Statutory Reports': { order: 50, kind: 'small', icon: DescriptionIcon, blurb: 'Schedule III / Suite' },
  'Asset Accounting': { order: 70, kind: 'small', icon: BusinessCenterIcon },
  'GST': { order: 80, kind: 'small', icon: PercentIcon, blurb: 'Compliance / Recon / Refund' },
  'Budget Control': { order: 90, kind: 'small', icon: BarChartIcon },
  'Supplier Invoice Mgmt': { order: 100, kind: 'small', icon: UploadFileIcon },
  'Cash Journal': { order: 110, kind: 'small', icon: SavingsIcon },
  'Controlling': { order: 120, kind: 'small', icon: ListAltIcon },
};
const WORK_TILE_ORDER = 60;   // the dark "My work" column sits between Statutory and Assets
const DEFAULT_ORDER = 900;

const QUICK_ACTIONS = [
  { label: 'Journal Entry', path: '/financial/journal-entries/new' },
  { label: 'Vendor Invoice', path: '/fi-ap/vendor-invoices/new', permission: 'fi_ap.view_payment' },
  { label: 'Payment Run', path: '/fi-ap/payment-runs', permission: 'fi_ap.view_payment' },
];

const groupLeaves = (group, canSeeSubGroup) => (
  group.subGroups
    ? group.subGroups.filter(canSeeSubGroup).flatMap(sg => sg.items || [])
    : (group.items || [])
);

const AccountingBentoMenu = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { hasPermission, isTenantAdmin } = usePermissions();
  const navGating = useNavigationGating();

  const [expanded, setExpanded] = useState(null);   // header of the tile showing its pages
  const [approvals, setApprovals] = useState({ state: 'loading', items: [] });
  const [recents, setRecents] = useState([]);

  useEffect(() => { setRecents(getRecentPages(user?.id)); }, [user?.id]);

  // Pending approvals - the one live figure on this page. A failure must read
  // as "not available", never as a zero: an approver shown 0 stops looking.
  useEffect(() => {
    let cancelled = false;
    api.get('/workflow/api/approvals/pending/')
      .then(res => {
        if (cancelled) return;
        const data = res.data;
        const items = Array.isArray(data) ? data : (data?.results || []);
        setApprovals({ state: 'ready', items });
      })
      .catch(() => { if (!cancelled) setApprovals({ state: 'unavailable', items: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Same visibility rules as the sidebar (ModernMainLayout.isSubVisible): the
  // navigation registry decides when it knows the entry, otherwise the compiled
  // permission does.
  const isSubVisible = useCallback((sub) => navGating.allows(
    CHILD_NAV_KEYS.get(sub), sub.path,
    () => isTenantAdmin || !sub.permission || hasPermission(sub.permission),
  ), [navGating, isTenantAdmin, hasPermission]);

  const canSeeSubGroup = useCallback(
    (sg) => isTenantAdmin || !sg.permission || hasPermission(sg.permission),
    [isTenantAdmin, hasPermission],
  );

  const tiles = useMemo(() => {
    const financial = sidebarSections
      .flatMap(section => section.items || [])
      .find(item => item.key === 'financial');
    if (!financial) return [];

    return (financial.submenuGroups || [])
      .map(group => {
        const pages = groupLeaves(group, canSeeSubGroup).filter(isSubVisible);
        const spec = TILE_SPEC[group.header] || {};
        return {
          header: group.header,
          pages,
          kind: spec.kind || 'small',
          icon: spec.icon || ListAltIcon,
          blurb: spec.blurb,
          order: spec.order === undefined ? DEFAULT_ORDER : spec.order,
        };
      })
      .filter(tile => tile.pages.length > 0)
      .sort((a, b) => a.order - b.order);
  }, [isSubVisible, canSeeSubGroup]);

  const quickActions = QUICK_ACTIONS.filter(
    a => isTenantAdmin || !a.permission || hasPermission(a.permission),
  );

  const go = (path) => () => navigate(path);
  const toggle = (key) => () => setExpanded(prev => (prev === key ? null : key));

  const linkSx = {
    cursor: 'pointer',
    color: INK_900,
    textDecoration: 'none',
    '&:hover': { color: ACCENT_700, textDecoration: 'underline' },
  };

  const countBadge = (n) => (
    <Box component="span" sx={{
      fontFamily: 'ui-monospace, Menlo, monospace',
      fontSize: 11, lineHeight: 1.4, color: ACCENT_600,
      border: '1px solid rgba(89,128,166,.4)', px: '6px', py: '2px', flexShrink: 0,
    }}>{n}</Box>
  );

  const renderHero = (tile) => {
    const Icon = tile.icon;
    const shown = tile.pages.slice(0, 10);
    const showingAll = expanded === tile.header;
    return (
      <Box key={tile.header} sx={{
        ...blueprintSx, gridColumn: { xs: 'span 1', sm: 'span 2' }, gridRow: 'span 2',
        p: '16px 18px', display: 'flex', flexDirection: 'column',
      }}>
        <Corners />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Icon sx={{ fontSize: 26, color: ACCENT }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 23, fontWeight: 600, lineHeight: 1.1, color: INK }}>
              {tile.header}
            </Typography>
            {tile.blurb && (
              <Typography sx={{ fontSize: 12.5, color: INK_700 }}>{tile.blurb}</Typography>
            )}
          </Box>
          {countBadge(tile.pages.length)}
        </Box>
        <Box sx={{ height: '1px', bgcolor: 'rgba(29,31,32,.12)', m: '14px 0 12px' }} />
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '7px 18px',
        }}>
          {(showingAll ? tile.pages : shown).map(sub => (
            <Box key={sub.path + sub.text} component="span" onClick={go(sub.path)} sx={{ ...linkSx, fontSize: 13.5 }}>
              {navGating.labelFor(CHILD_NAV_KEYS.get(sub), sub.text)}
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        {tile.pages.length > shown.length && (
          <Box onClick={toggle(tile.header)} sx={{
            mt: 1.5, fontSize: 12, fontWeight: 600, letterSpacing: '.1em',
            textTransform: 'uppercase', color: ACCENT_600, cursor: 'pointer',
            '&:hover': { color: ACCENT_700 },
          }}>
            {showingAll ? 'Show fewer' : `All ${tile.pages.length} pages →`}
          </Box>
        )}
      </Box>
    );
  };

  // The hover reveal is CSS-only and absolutely positioned, for one reason: a
  // reveal that grows the tile reflows the grid, which moves the tile out from
  // under the pointer -> mouseleave -> it shrinks back -> mouseenter -> grows.
  // Driven from React state that oscillated forever ("Maximum update depth
  // exceeded"). Keep both properties: no state on the hover path, and the
  // panel must never change the tile's own box.
  const revealPanelSx = {
    display: 'none',
    position: 'absolute',
    top: '100%', left: '-1px', right: '-1px',
    zIndex: 3,
    bgcolor: '#fff',
    border: `1px solid ${DIVIDER}`,
    borderTop: `1px solid ${ACCENT}`,
    boxShadow: '0 12px 32px rgba(43,43,45,.22)',
    p: '10px 14px 12px',
    flexWrap: 'wrap',
    gap: '4px 12px',
    lineHeight: 1.6,
  };
  const revealOnHoverSx = {
    '&:hover': { boxShadow: '0 3px 10px rgba(43,43,45,.16)', zIndex: 2 },
    '&:hover .reveal, &:focus-within .reveal': { display: 'flex' },
  };

  const revealPanel = (pages) => (
    <Box className="reveal" sx={revealPanelSx}>
      {pages.map(sub => (
        <Box
          key={sub.path + sub.text}
          component="span"
          onClick={(e) => { e.stopPropagation(); navigate(sub.path); }}
          sx={{ ...linkSx, color: ACCENT_700, fontSize: 12.5 }}
        >
          {navGating.labelFor(CHILD_NAV_KEYS.get(sub), sub.text)}
        </Box>
      ))}
    </Box>
  );

  const renderWide = (tile) => {
    const Icon = tile.icon;
    const shown = tile.pages.slice(0, 6);
    return (
      <Box
        key={tile.header}
        sx={{
          ...blueprintSx, ...revealOnHoverSx,
          gridColumn: { xs: 'span 1', sm: 'span 2' },
          p: '14px 18px', display: 'flex', flexDirection: 'column',
          transition: 'box-shadow .15s',
        }}
      >
        <Corners />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
          <Icon sx={{ fontSize: 22, color: ACCENT }} />
          <Typography sx={{ fontSize: 21, fontWeight: 600, color: INK }}>{tile.header}</Typography>
          <Box sx={{ flex: 1 }} />
          {countBadge(tile.pages.length)}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', mt: 1.4 }}>
          {shown.map(sub => (
            <Box
              key={sub.path + sub.text}
              component="span"
              onClick={() => navigate(sub.path)}
              sx={{ ...linkSx, fontSize: 13 }}
            >
              {navGating.labelFor(CHILD_NAV_KEYS.get(sub), sub.text)}
            </Box>
          ))}
          {tile.pages.length > shown.length && (
            <Box component="span" sx={{ fontSize: 13, color: ACCENT_600 }}>
              {`+${tile.pages.length - shown.length} more`}
            </Box>
          )}
        </Box>
        {tile.pages.length > shown.length && revealPanel(tile.pages)}
      </Box>
    );
  };

  const renderSmall = (tile) => {
    const Icon = tile.icon;
    return (
      <Box
        key={tile.header}
        sx={{
          ...blueprintSx, ...revealOnHoverSx,
          p: '14px', display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between', gap: 1, minHeight: 126,
          transition: 'box-shadow .15s',
        }}
      >
        <Corners />
        <Icon sx={{ fontSize: 22, color: ACCENT }} />
        <Box>
          <Typography sx={{ fontSize: 19, fontWeight: 600, color: INK, lineHeight: 1.15 }}>
            {tile.header}
          </Typography>
          <Typography sx={{ fontSize: 12, color: INK_700 }}>
            {tile.blurb || `${tile.pages.length} page${tile.pages.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>
        {revealPanel(tile.pages)}
      </Box>
    );
  };

  const renderWorkTile = () => {
    const { state, items } = approvals;
    const refOf = (a) => (a && a.step_instance && a.step_instance.workflow_instance
      && a.step_instance.workflow_instance.reference_number) || 'Approval';
    const nameOf = (a) => {
      const si = a && a.step_instance;
      const wi = si && si.workflow_instance;
      const wv = wi && wi.workflow_version;
      return (wv && wv.workflow && wv.workflow.name)
        || (si && si.workflow_step && si.workflow_step.name)
        || '';
    };
    return (
      <Box key="__work__" sx={{
        ...blueprintSx, bgcolor: ACCENT_900, color: '#fff',
        gridRow: { xs: 'span 1', sm: 'span 3' }, p: '15px',
        display: 'flex', flexDirection: 'column',
      }}>
        <Corners />
        <Typography sx={{
          fontSize: 12, fontWeight: 600, letterSpacing: '.12em',
          textTransform: 'uppercase', color: ACCENT_300,
        }}>My work</Typography>

        {state === 'loading' && (
          <Typography sx={{ fontSize: 13, color: '#d6ebff', mt: 1 }}>Loading...</Typography>
        )}
        {state === 'unavailable' && (
          <Typography sx={{ fontSize: 13, color: '#d6ebff', mt: 1, lineHeight: 1.5 }}>
            Pending approvals could not be read - count not available.
          </Typography>
        )}
        {state === 'ready' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
              <Typography sx={{ fontSize: 40, fontWeight: 600, lineHeight: 1 }}>{items.length}</Typography>
              <Typography sx={{ fontSize: 13, color: '#d6ebff' }}>pending approvals</Typography>
            </Box>
            {items.length > 0 && (
              <>
                <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,.18)', m: '12px 0' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
                  {items.slice(0, 4).map((a, i) => (
                    <Box key={a.id || i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 12.5 }}>
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {refOf(a)}
                      </Box>
                      <Box component="span" sx={{
                        color: ACCENT_400, flexShrink: 0, maxWidth: '45%',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {nameOf(a)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}

        <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,.18)', m: '12px 0' }} />
        <Typography sx={{
          fontSize: 12, fontWeight: 600, letterSpacing: '.12em',
          textTransform: 'uppercase', color: ACCENT_300,
        }}>Recently visited</Typography>
        {recents.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: '#d6ebff', mt: 1, lineHeight: 1.5 }}>
            Nothing yet - pages you open appear here.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, mt: 1, color: '#eef6ff' }}>
            {recents.slice(0, 4).map(r => (
              <Box key={r.path} component="span" onClick={go(r.path)} sx={{
                cursor: 'pointer', fontSize: 12.5,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                '&:hover': { color: ACCENT_300, textDecoration: 'underline' },
              }}>{r.label}</Box>
            ))}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <Box onClick={go('/workflow/my-approvals')} sx={{
          mt: 1.5, fontSize: 12, fontWeight: 600, letterSpacing: '.1em',
          textTransform: 'uppercase', color: ACCENT_400, cursor: 'pointer',
          '&:hover': { color: '#fff' },
        }}>{'Open approvals →'}</Box>
      </Box>
    );
  };

  const rendered = [];
  let workPlaced = false;
  tiles.forEach(tile => {
    if (!workPlaced && tile.order > WORK_TILE_ORDER) {
      rendered.push(renderWorkTile());
      workPlaced = true;
    }
    if (tile.kind === 'hero') rendered.push(renderHero(tile));
    else if (tile.kind === 'wide') rendered.push(renderWide(tile));
    else rendered.push(renderSmall(tile));
  });
  if (!workPlaced) rendered.push(renderWorkTile());

  return (
    <Box sx={{
      // Full-bleed against ModernMainLayout's own padding (px xs:2/sm:3, py:3)
      // so the launcher reads as the module surface rather than a card on it.
      bgcolor: PAGE_BG,
      mx: { xs: -2, sm: -3 }, mt: -3, mb: -3,
      p: { xs: '18px 16px', md: '24px 28px' },
      minHeight: '100%',
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 2, mb: 2.5,
      }}>
        <Box>
          <Typography sx={{
            fontSize: 12, fontWeight: 600, letterSpacing: '.14em',
            textTransform: 'uppercase', color: ACCENT_600,
          }}>Finance &amp; Admin</Typography>
          <Typography component="h1" sx={{
            fontSize: 34, fontWeight: 600, lineHeight: 1.05,
            letterSpacing: '-.015em', color: INK,
          }}>Accounting</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {quickActions.map((a, i) => {
            const primary = i === 0;
            return (
              <Box key={a.path} onClick={go(a.path)} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                px: '13px', py: '7px', fontSize: 13, fontWeight: 600,
                border: `1px solid ${primary ? ACCENT : DIVIDER}`,
                bgcolor: primary ? ACCENT : 'transparent',
                color: primary ? '#fff' : INK,
                '&:hover': { bgcolor: primary ? ACCENT_700 : 'rgba(29,31,32,.06)' },
              }}>
                <AddIcon sx={{ fontSize: 14 }} />{a.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {tiles.length === 0 ? (
        <Box sx={{ ...blueprintSx, p: 3 }}>
          <Corners />
          <Typography sx={{ fontSize: 14, color: INK_700 }}>
            No Accounting pages are available to you. Ask an administrator for access to
            the Accounting module &mdash; <code>fi.view_gl_account</code>, plus the module
            permissions for the areas you need.
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gridAutoRows: 'minmax(126px, auto)',
          gap: 2,
        }}>
          {rendered}
        </Box>
      )}

      <Box onClick={go('/financial')} sx={{
        mt: 3, display: 'inline-flex', alignItems: 'center', gap: 0.75,
        fontSize: 12.5, color: ACCENT_700, cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
      }}>
        <GridViewIcon sx={{ fontSize: 15 }} />
        Financial Accounting dashboard
        <ArrowForwardIcon sx={{ fontSize: 14 }} />
      </Box>
    </Box>
  );
};

export default AccountingBentoMenu;
